import { __rest } from "tslib";
import * as React from "react";
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { useSticky } from "../../components/hooks/use-sticky";
/**
 * @example
 *
 * ```jsx
 * import {
 *   Pagination,
 *   PaginationContent,
 *   PaginationEllipsis,
 *   PaginationItem,
 *   PaginationLink,
 *   PaginationNext,
 *   PaginationPrevious,
 * } from "@skve/components"
 *
 * function Demo() {
 *   return (
 *     <Pagination>
 *       <PaginationContent>
 *         <PaginationItem>
 *           <PaginationPrevious href="#" />
 *         </PaginationItem>
 *         <PaginationItem>
 *           <PaginationLink href="#">1</PaginationLink>
 *         </PaginationItem>
 *         <PaginationItem>
 *           <PaginationLink href="#" isActive>
 *             2
 *           </PaginationLink>
 *         </PaginationItem>
 *         <PaginationItem>
 *           <PaginationLink href="#">3</PaginationLink>
 *         </PaginationItem>
 *         <PaginationItem>
 *           <PaginationEllipsis />
 *         </PaginationItem>
 *         <PaginationItem>
 *           <PaginationNext href="#" />
 *         </PaginationItem>
 *       </PaginationContent>
 *     </Pagination>
 *   )
 * }
 * ```
 */
const Pagination = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement("nav", Object.assign({ role: "navigation", "aria-label": "pagination", className: cn("mx-auto flex w-full justify-center", className) }, props)));
};
Pagination.displayName = "Pagination";
const PaginationContent = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement("ul", Object.assign({ ref: ref, className: cn("flex flex-row items-center gap-1", className) }, props)));
});
PaginationContent.displayName = "PaginationContent";
const PaginationItem = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement("li", Object.assign({ ref: ref, className: cn("", className) }, props)));
});
PaginationItem.displayName = "PaginationItem";
const PaginationLink = (_a) => {
    var { className, isActive, size = "icon", type } = _a, props = __rest(_a, ["className", "isActive", "size", "type"]);
    return (React.createElement(Button, Object.assign({ "aria-current": isActive ? "page" : undefined, variant: isActive ? "outline" : "ghost", size: size, className: className }, props)));
};
PaginationLink.displayName = "PaginationLink";
const PaginationPrevious = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement(PaginationLink, Object.assign({ "aria-label": "Go to previous page", size: "default", className: cn("gap-1 pl-2.5", className) }, props),
        React.createElement(ChevronLeft, { className: "h-4 w-4" }),
        React.createElement("span", null, "Zur\u00FCck")));
};
PaginationPrevious.displayName = "PaginationPrevious";
const PaginationNext = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement(PaginationLink, Object.assign({ "aria-label": "Go to next page", size: "default", className: cn("gap-1 pr-2.5", className) }, props),
        React.createElement("span", null, "Weiter"),
        React.createElement(ChevronRight, { className: "h-4 w-4" })));
};
PaginationNext.displayName = "PaginationNext";
const PaginationEllipsis = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement("span", Object.assign({ "aria-hidden": true, className: cn("flex h-9 w-9 items-center justify-center", className) }, props),
        React.createElement(MoreHorizontal, { className: "h-4 w-4" }),
        React.createElement("span", { className: "sr-only" }, "Mehr")));
};
PaginationEllipsis.displayName = "PaginationEllipsis";
function getCards(page, total, maxCards = 5) {
    const cards = [];
    for (let i = 1; i <= total; i++) {
        cards.push(i);
    }
    if (cards.length > maxCards) {
        const idx = cards.indexOf(page);
        if (idx + 2 < cards.length) {
            const end = cards.length - 1;
            const start = 1 + Math.max(2, idx);
            const length = end - start;
            cards.splice(start, length, "...");
        }
        if (cards.length > maxCards && idx - 2 > 0) {
            const length = Math.min(idx - 1, 1 + cards.length - maxCards);
            cards.splice(1, length, "...");
        }
    }
    return cards;
}
function useCards(page, total) {
    return React.useMemo(() => getCards(page, total), [page, total]);
}
const DefaultPagination = ({ page, setPage, totalPages, }) => {
    const cards = useCards(page, totalPages);
    React.useEffect(() => {
        const handler = (ev) => {
            if (ev.ctrlKey && ev.shiftKey) {
                if (ev.target instanceof HTMLInputElement ||
                    ev.target instanceof HTMLTextAreaElement) {
                    // empty on purpose
                }
                else if (ev.key === "ArrowLeft") {
                    setPage(Math.max(1, page - 1));
                    ev.preventDefault();
                }
                else if (ev.key === "ArrowRight") {
                    setPage(Math.min(totalPages, page + 1));
                    ev.preventDefault();
                }
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [page, totalPages]);
    return (React.createElement(Pagination, { className: "mt-4" },
        React.createElement(PaginationContent, null,
            React.createElement(PaginationItem, null,
                React.createElement(PaginationPrevious, { disabled: page === 1, onClick: () => setPage(page - 1) })),
            cards.map((card, i) => (React.createElement(PaginationItem, { key: `${card}_${i}` }, typeof card === "string" ? (React.createElement(PaginationEllipsis, { className: "max-[420px]:hidden" })) : (React.createElement(PaginationLink, { isActive: card === page, onClick: () => setPage(card), className: card !== page ? "max-[420px]:hidden" : undefined }, card))))),
            React.createElement(PaginationItem, null,
                React.createElement(PaginationNext, { disabled: page === totalPages, onClick: () => setPage(page + 1) })))));
};
const StickyPagination = (props) => {
    const ref = useSticky("bottom", 16);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", null),
        React.createElement("div", { ref: ref, className: "pb-4" },
            React.createElement(DefaultPagination, Object.assign({}, props)))));
};
export { DefaultPagination, StickyPagination, Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, };
//# sourceMappingURL=pagination.js.map